import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FiArrowLeft, FiUpload, FiTrash2, FiExternalLink } from 'react-icons/fi';
import API from '../utils/api';
import toast from 'react-hot-toast';
import { useStaffLog } from '../utils/useStaffLog';

const EMPTY_FORM = {
  expense_category_id: '',
  expense_date:        new Date().toISOString().slice(0, 10),
  description:         '',
  payment_method:      'cash',
  bank_account_id:     '',
  reference_number:    '',
  taxable_amount:      '',
  other_charges:       '',
  round_off:           '',
  notes:               '',
};

const DOC_LABEL = { receipt: 'Receipt', bill: 'Bill', invoice: 'Invoice', other: 'Other' };

export default function AdminExpenseForm() {
  const navigate  = useNavigate();
  const { log } = useStaffLog();
  const { id }    = useParams(); // undefined → create mode
  const isEdit        = Boolean(id);
  const [bankAccounts, setBankAccounts] = useState([]);

  useEffect(() => {
    API.get('/company-bank-accounts', { params: { active: 'true' } })
      .then(({ data }) => setBankAccounts(data.accounts || []))
      .catch(() => {});
  }, []);

  const [form,        setForm]        = useState(EMPTY_FORM);
  const [categories,  setCategories]  = useState([]);
  const [selectedCat, setSelectedCat] = useState(null);
  const [documents,   setDocuments]   = useState([]); // existing docs (edit mode)
  const [saving,      setSaving]      = useState(false);
  const [uploading,   setUploading]   = useState(false);

  // Derived totals (live preview)
  const taxable    = Number(form.taxable_amount)  || 0;
  const charges    = Number(form.other_charges)   || 0;
  const roundOff   = Number(form.round_off)       || 0;
  const grandTotal = Number((taxable + charges + roundOff).toFixed(2));

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const fetchCategories = useCallback(async () => {
    try {
      const { data } = await API.get('/expenses/categories', { params: { status: 'active' } });
      setCategories(data.categories || []);
    } catch {
      toast.error('Failed to load categories');
    }
  }, []);

  const fetchExpense = useCallback(async () => {
    if (!isEdit) return;
    try {
      const { data } = await API.get(`/expenses/${id}`);
      const exp = data.expense;
      setForm({
        expense_category_id: exp.expense_category_id,
        expense_date:        exp.expense_date,
        description:         exp.description,
        payment_method:      exp.payment_method,
        bank_account_id:     exp.bank_account_id ? String(exp.bank_account_id) : '',
        reference_number:    exp.reference_number || '',
        taxable_amount:      exp.taxable_amount,
        other_charges:       exp.other_charges || '',
        round_off:           exp.round_off || '',
        notes:               exp.notes || '',
      });
      setDocuments(exp.documents || []);
    } catch {
      toast.error('Failed to load expense');
    }
  }, [id, isEdit]);

  useEffect(() => { fetchCategories(); }, [fetchCategories]);
  useEffect(() => { fetchExpense(); }, [fetchExpense]);

  // Keep selectedCat in sync when categories load or form.expense_category_id changes
  useEffect(() => {
    const cat = categories.find(c => String(c.id) === String(form.expense_category_id));
    setSelectedCat(cat || null);
  }, [categories, form.expense_category_id]);

  const handleSubmit = async () => {
    if (!form.expense_category_id) return toast.error('Please select a category');
    if (!form.expense_date)        return toast.error('Expense date is required');
    if (!form.description.trim())  return toast.error('Description is required');
    if (!form.payment_method)      return toast.error('Payment method is required');
    if (!form.taxable_amount || Number(form.taxable_amount) <= 0) return toast.error('Amount must be greater than 0');

    const payload = {
      ...form,
      taxable_amount: Number(form.taxable_amount),
      other_charges:  Number(form.other_charges) || 0,
      round_off:      Number(form.round_off) || 0,
    };

    try {
      setSaving(true);
      if (isEdit) {
        await API.put(`/expenses/${id}`, payload);
        toast.success('Expense updated');
        await log('updated_expense', `Updated expense: ${form.description}`, 'Expense', Number(id));
        navigate(`/admin/expenses/${id}`);
      } else {
        const { data } = await API.post('/expenses', payload);
        toast.success('Expense created');
        await log('created_expense', `Created expense: ${form.description} (₹${form.taxable_amount})`, 'Expense', data.expense.id);
        navigate(`/admin/expenses/${data.expense.id}`);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save expense');
    } finally {
      setSaving(false);
    }
  };

  // Document upload
  const handleDocUpload = async (e) => {
    if (!isEdit) return;
    const file = e.target.files?.[0];
    if (!file) return;

    const docType = window.prompt('Document type (receipt / bill / invoice / other):', 'receipt') || 'other';

    try {
      setUploading(true);
      const formData = new FormData();
      formData.append('image', file); // reuse existing upload endpoint
      const { data: upload } = await API.post('/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      await API.post(`/expenses/${id}/documents`, {
        document_type: docType,
        document_name: file.name,
        file_path:     upload.url,
      });
      toast.success('Document uploaded');
      fetchExpense();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Upload failed');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleDocDelete = async (docId) => {
    if (!window.confirm('Remove this document?')) return;
    try {
      await API.delete(`/expenses/${id}/documents/${docId}`);
      toast.success('Document removed');
      fetchExpense();
    } catch {
      toast.error('Failed to remove document');
    }
  };

  const inputStyle = {
    width: '100%', boxSizing: 'border-box',
    padding: '9px 12px', border: '1px solid #e5e7eb',
    borderRadius: 8, fontSize: 14, outline: 'none',
  };
  const labelStyle = { display: 'block', fontSize: 12, fontWeight: 600, color: '#6b7280', marginBottom: 5 };
  const sectionStyle = { background: '#fff', border: '1px solid var(--border)', borderRadius: 12, padding: 24, marginBottom: 20 };

  return (
    <div style={{ padding: 24, maxWidth: 760, margin: '0 auto' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
        <button
          onClick={() => navigate(isEdit ? `/admin/expenses/${id}` : '/admin/expenses')}
          style={{ background: 'none', border: '1px solid #e5e7eb', borderRadius: 8, padding: '7px 10px', cursor: 'pointer', display: 'flex', alignItems: 'center' }}
        >
          <FiArrowLeft size={15} />
        </button>
        <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 700 }}>
          {isEdit ? 'Edit Expense' : 'New Expense'}
        </h2>
      </div>

      {/* Basic Info */}
      <div style={sectionStyle}>
        <h3 style={{ margin: '0 0 18px', fontSize: 15, fontWeight: 700 }}>Expense Details</h3>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <div>
            <label style={labelStyle}>Category *</label>
            <select
              style={{ ...inputStyle, background: '#fff' }}
              value={form.expense_category_id}
              onChange={e => set('expense_category_id', e.target.value)}
            >
              <option value="">— Select category —</option>
              {categories.map(c => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label style={labelStyle}>Expense Date *</label>
            <input type="date" style={inputStyle} value={form.expense_date} onChange={e => set('expense_date', e.target.value)} />
          </div>
          <div style={{ gridColumn: '1 / -1' }}>
            <label style={labelStyle}>Description *</label>
            <input style={inputStyle} value={form.description} onChange={e => set('description', e.target.value)} placeholder="Brief description of the expense" />
          </div>
          <div>
            <label style={labelStyle}>Payment Method *</label>
            <select style={{ ...inputStyle, background: '#fff' }} value={form.payment_method} onChange={e => { set('payment_method', e.target.value); set('bank_account_id', ''); }}>
              <option value="cash">Cash</option>
              <option value="upi">UPI</option>
              <option value="bank_transfer">Bank Transfer</option>
              <option value="cheque">Cheque</option>
              <option value="other">Other</option>
            </select>
          </div>
          <div>
            <label style={labelStyle}>Paid From Account</label>
            <select style={{ ...inputStyle, background: '#fff' }} value={form.bank_account_id} onChange={e => set('bank_account_id', e.target.value)}>
              <option value="">— Select Account —</option>
              {bankAccounts
                .filter(a => {
                  if (form.payment_method === 'cash')         return a.account_type === 'cash';
                  if (form.payment_method === 'upi')          return a.account_type === 'upi';
                  if (form.payment_method === 'bank_transfer' || form.payment_method === 'cheque') return a.account_type === 'bank';
                  return true;
                })
                .map(a => (
                  <option key={a.id} value={a.id}>
                    {a.account_name}{a.account_number_last4 ? ` (••••${a.account_number_last4})` : ''}
                  </option>
                ))
              }
            </select>
          </div>
          <div>
            <label style={labelStyle}>Reference Number</label>
            <input style={inputStyle} value={form.reference_number} onChange={e => set('reference_number', e.target.value)} placeholder="Cheque no., UPI ref, etc." />
          </div>
        </div>
      </div>

      {/* Financials */}
      <div style={sectionStyle}>
        <h3 style={{ margin: '0 0 18px', fontSize: 15, fontWeight: 700 }}>Amount & Tax</h3>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <div>
            <label style={labelStyle}>Taxable Amount (₹) *</label>
            <input
              type="number" min="0" step="0.01"
              style={inputStyle}
              value={form.taxable_amount}
              onChange={e => set('taxable_amount', e.target.value)}
              placeholder="0.00"
            />
          </div>

          <div>
            <label style={labelStyle}>Other Charges (₹)</label>
            <input
              type="number" step="0.01"
              style={inputStyle}
              value={form.other_charges}
              onChange={e => set('other_charges', e.target.value)}
              placeholder="0.00"
            />
          </div>
          <div>
            <label style={labelStyle}>Round Off (₹)</label>
            <input
              type="number" step="0.01"
              style={inputStyle}
              value={form.round_off}
              onChange={e => set('round_off', e.target.value)}
              placeholder="e.g. 0.50 or -0.30"
            />
          </div>
        </div>

        {/* Total preview */}
        <div style={{ marginTop: 20, padding: 16, background: '#f0fdf4', borderRadius: 10, border: '1px solid #bbf7d0' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {[
              { label: 'Taxable Amount', val: taxable },
              ...(charges ? [{ label: 'Other Charges', val: charges }] : []),
              ...(roundOff ? [{ label: 'Round Off', val: roundOff }] : []),
            ].map(row => (
              <div key={row.label} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: '#374151' }}>
                <span>{row.label}</span>
                <span>₹{Number(row.val).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
              </div>
            ))}
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 16, fontWeight: 700, color: '#16a34a', borderTop: '1px solid #bbf7d0', paddingTop: 8, marginTop: 4 }}>
              <span>Grand Total</span>
              <span>₹{grandTotal.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Notes */}
      <div style={sectionStyle}>
        <h3 style={{ margin: '0 0 14px', fontSize: 15, fontWeight: 700 }}>Notes</h3>
        <textarea
          style={{ ...inputStyle, resize: 'vertical', minHeight: 80 }}
          value={form.notes}
          onChange={e => set('notes', e.target.value)}
          placeholder="Any additional notes…"
        />
      </div>

      {/* Documents (edit mode only) */}
      {isEdit && (
        <div style={sectionStyle}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700 }}>Documents</h3>
            <label style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '7px 14px', background: 'var(--primary)', color: '#fff', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
              <FiUpload size={13} />
              {uploading ? 'Uploading…' : 'Upload'}
              <input type="file" style={{ display: 'none' }} onChange={handleDocUpload} accept=".pdf,.jpg,.jpeg,.png" />
            </label>
          </div>
          {documents.length === 0 ? (
            <p style={{ margin: 0, color: 'var(--text-muted)', fontSize: 13 }}>No documents attached yet.</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {documents.map(doc => (
                <div key={doc.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', background: '#f9fafb', borderRadius: 8, border: '1px solid #f3f4f6' }}>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 600 }}>{doc.document_name}</div>
                    <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 2 }}>{DOC_LABEL[doc.document_type] || doc.document_type}</div>
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <a href={doc.file_path} target="_blank" rel="noopener noreferrer" style={{ color: '#6b7280', display: 'flex', alignItems: 'center' }}>
                      <FiExternalLink size={15} />
                    </a>
                    <button onClick={() => handleDocDelete(doc.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', display: 'flex', alignItems: 'center' }}>
                      <FiTrash2 size={15} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Actions */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
        <button
          onClick={() => navigate(isEdit ? `/admin/expenses/${id}` : '/admin/expenses')}
          style={{ padding: '10px 22px', border: '1px solid #e5e7eb', borderRadius: 8, background: '#fff', cursor: 'pointer', fontSize: 14 }}
        >
          Cancel
        </button>
        <button
          onClick={handleSubmit}
          disabled={saving}
          style={{ padding: '10px 26px', background: 'var(--primary)', color: '#fff', border: 'none', borderRadius: 8, cursor: saving ? 'not-allowed' : 'pointer', fontSize: 14, fontWeight: 600, opacity: saving ? 0.7 : 1 }}
        >
          {saving ? 'Saving…' : isEdit ? 'Update Expense' : 'Create Expense'}
        </button>
      </div>
    </div>
  );
}